/*-
 * #%L
 * Aires Éducatives
 * %%
 * Copyright (C) 2020 - 2023 OFB
 * %%
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 * #L%
 */
import {
  ActualiteDTO,
  ActualiteTypeDTO,
  FilterDTO,
  PaginationParameter,
  PaginationResult,
} from "@/model/bean/GeneratedDTOs"
import ConversionUtils from "@/services/ConversionUtils"
import { AbstractTypedRestService } from "@/services/rest/AbstractTypedRestService"

export class ActualiteService extends AbstractTypedRestService<ActualiteDTO> {
  static INSTANCE = new ActualiteService()

  async getActualitesNationalesOnly(
    paginationParameters: PaginationParameter,
    filtersList: Array<FilterDTO>,
  ): Promise<PaginationResult<ActualiteDTO>> {
    const paginatedResult = await super.typeGetPaginatedV2(
      "actualites",
      2,
      paginationParameters,
      filtersList,
    )
    paginatedResult.elements = paginatedResult.elements.map((actu) =>
      ConversionUtils.convertActualiteDatesToUTC(actu as ActualiteDTO),
    )
    return paginatedResult as PaginationResult<ActualiteDTO>
  }

  async createActualiteNationale(actualite: ActualiteDTO): Promise<ActualiteDTO> {
    return super.typedPostV2("actualites", 2, ConversionUtils.convertActualiteDatesToUTC(actualite))
  }

  async updateActualiteNationale(actualite: ActualiteDTO): Promise<ActualiteDTO> {
    return super.typedPutV2(
      "actualites/" + actualite.id,
      2,
      ConversionUtils.convertActualiteDatesToUTC(actualite),
    )
  }

  async deleteActualiteNationale(actualite: ActualiteDTO): Promise<ActualiteDTO> {
    return super.typedDeleteSingleV2("actualites/" + actualite.id, 2)
  }

  async getAllActualitesForGRAE(
    graeId: string,
    paginationParameters: PaginationParameter,
    filtersList: Array<FilterDTO>,
  ): Promise<PaginationResult<ActualiteDTO>> {
    const paginatedResult = await super.typeGetPaginatedV2(
      "grae/" + graeId + "/actualites",
      2,
      paginationParameters,
      filtersList,
    )
    paginatedResult.elements = paginatedResult.elements.map((actu) =>
      ConversionUtils.convertActualiteDatesToUTC(actu as ActualiteDTO),
    )
    return paginatedResult as PaginationResult<ActualiteDTO>
  }

  async createActualiteForGRAE(actualite: ActualiteDTO, graeId: string): Promise<ActualiteDTO> {
    return super.typedPostV2(
      "grae/" + graeId + "/actualites",
      2,
      ConversionUtils.convertActualiteDatesToUTC(actualite),
    )
  }

  async updateActualiteForGRAE(actualite: ActualiteDTO, graeId: string): Promise<ActualiteDTO> {
    return super.typedPutV2(
      "grae/" + graeId + "/actualites/" + actualite.id,
      2,
      ConversionUtils.convertActualiteDatesToUTC(actualite),
    )
  }

  async deleteActualiteForGRAE(actualite: ActualiteDTO, graeId: string): Promise<ActualiteDTO> {
    return super.typedDeleteSingleV2("grae/" + graeId + "/actualites/" + actualite.id, 2)
  }

  async getAllActualiteTypes(): Promise<ActualiteTypeDTO[]> {
    return await super.typedGetV2<ActualiteTypeDTO>("actualites/type", 2)
  }

  async saveActualiteType(actualiteType: ActualiteTypeDTO): Promise<ActualiteTypeDTO> {
    return super.typedPostV2("actualites/type", 2, actualiteType)
  }

  async deleteActualiteType(actualiteTypeId: string): Promise<string> {
    return super.typedDeleteSingleV2("actualites/type/" + actualiteTypeId, 2)
  }
}
